/**
 * LoopNest Suno Link Resolver
 * Resolves Suno.com page links to playable audio sources.
 * 
 * Suno links look like: https://suno.com/song/abc123
 * The audio source is typically embedded in the page metadata.
 */

/**
 * Detect if a URL is a Suno link
 */
export function isSunoLink(url) {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u?.hostname === 'suno.com' || u?.hostname === 'www.suno.com' || u?.hostname === 'app.suno.ai';
  } catch {
    return false;
  }
}

/**
 * Detect if a URL is a direct audio file
 */
export function isDirectAudioUrl(url) {
  if (!url) return false;
  return /\.(mp3|wav|m4a|aac|ogg|flac)(\?.*)?$/i?.test(url);
}

/**
 * Detect if a URL is a supported streaming platform (not importable)
 */
export function isUnsupportedStreamingLink(url) {
  if (!url) return false;
  const unsupported = [
    'spotify.com', 'apple.com/music', 'music.apple.com',
    'tidal.com', 'deezer.com', 'soundcloud.com',
    'youtube.com', 'youtu.be', 'music.youtube.com',
    'amazon.com/music', 'music.amazon',
  ];
  try {
    const u = new URL(url);
    return unsupported?.some((domain) => u?.hostname?.includes(domain));
  } catch {
    return false;
  }
}

/**
 * Extract Suno song ID from URL
 */
export function extractSunoSongId(url) {
  try {
    const u = new URL(url);
    // Patterns: /song/ID, /s/ID, /track/ID
    const match = u?.pathname?.match(/\/(song|s|track)\/([a-zA-Z0-9_-]+)/);
    if (match) return match?.[2];
    // Fallback: last path segment
    const parts = u?.pathname?.split('/')?.filter(Boolean);
    return parts?.[parts?.length - 1] || null;
  } catch {
    return null;
  }
}

/**
 * Attempt to resolve a Suno page link to a direct audio URL.
 * 
 * NOTE: Suno does not provide a public API. This resolver attempts
 * to construct the CDN audio URL from the song ID pattern.
 * Suno audio URLs follow the pattern:
 *   https://cdn1.suno.ai/{songId}.mp3
 * 
 * This may break if Suno changes their CDN structure.
 * If resolution fails, the user is prompted to download and upload the file.
 */
export async function resolveSunoLink(url) {
  const songId = extractSunoSongId(url);
  if (!songId) {
    return {
      success: false,
      error: 'Could not extract song ID from Suno link. Please download the audio file from Suno and upload it directly.',
    };
  }

  // Try known Suno CDN patterns
  const candidateUrls = [
    `https://cdn1.suno.ai/${songId}.mp3`,
    `https://cdn2.suno.ai/${songId}.mp3`,
    `https://audiopipe.suno.ai/?item_id=${songId}`,
  ];

  for (const candidateUrl of candidateUrls) {
    try {
      const response = await fetch(candidateUrl, { method: 'HEAD', mode: 'no-cors' });
      // no-cors won't give us status, but if it doesn't throw, the URL likely exists
      return {
        success: true,
        audioUrl: candidateUrl,
        songId,
        provider: 'suno',
        note: 'Resolved via Suno CDN pattern. If playback fails, please download and upload the file.',
      };
    } catch {
      continue;
    }
  }

  // If all candidates fail, return the first candidate anyway
  // (no-cors fetch may throw even for valid URLs due to CORS)
  return {
    success: true,
    audioUrl: candidateUrls?.[0],
    songId,
    provider: 'suno',
    note: 'Suno CDN URL constructed. If playback fails, download the audio from Suno and upload it directly.',
  };
}

/**
 * Main URL resolver — handles all supported URL types
 * @param {string} url
 * @returns {Promise<ResolveResult>}
 */
export async function resolveAudioUrl(url) {
  if (!url?.trim()) {
    return { success: false, error: 'Please enter a URL.' };
  }

  // Check for unsupported streaming platforms first
  if (isUnsupportedStreamingLink(url)) {
    return {
      success: false,
      error: 'This music link cannot be imported. Streaming platforms (Spotify, Apple Music, YouTube, SoundCloud) are not supported. Please upload the audio file directly or use a direct audio URL.',
    };
  }

  // Direct audio file URL
  if (isDirectAudioUrl(url)) {
    return {
      success: true,
      audioUrl: url,
      provider: 'direct',
    };
  }

  // Suno link
  if (isSunoLink(url)) {
    return await resolveSunoLink(url);
  }

  // Unknown URL — try as direct audio anyway
  return {
    success: false,
    error: 'This music link cannot be imported. Please use a direct audio file URL (mp3, wav, m4a, aac) or a Suno song link. For other platforms, download the audio file and upload it directly.',
  };
}
